import { http } from './http'
import { auth } from './auth'
import { z } from 'zod'
import { UploadRawUrlSchema, UploadSchema } from '../schemas'
import { UploadRawUrl } from '../types'
import { parseWithZod } from '../utils/parse-with-zod'

const UploadResponseSchema = z.object({
  uploads: z.array(UploadSchema),
})

const CompleteUploadSchema = z.object({
  upload: UploadSchema,
})

const dealResponseSchema = z.object({
  deal: z.object({
    id: z.number(),
  }),
})

export const upload = {
  uploadFile: async (
    dealId: number,
    payload: {
      filename: string
    },
  ) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(`${regionHost}/api/deals/${dealId}/upload`, payload)
    return parseWithZod(UploadFileResponseSchema, result)
  },

  uploadFileToS3: async (payload: {
    file: File
    upload_url: string
    contentType?: string
  }): Promise<Response> => {
    return http.putFile(
      payload.upload_url,
      payload.file,
      payload.contentType || 'binary/octet-stream',
    ) as Promise<Response>
  },

  completeUpload: async (payload: CompleteUploadPayload, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/uploads/${payload.upload_id}/complete`,
      {
        md5: payload.md5,
        size: payload.size,
        should_match: payload.should_match,
      },
      dealId,
    )
    return parseWithZod(CompleteUploadSchema, result)
  },

  failUpload: async (uploadId: number, errorReason: string, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    return http.post(
      `${regionHost}/api/uploads/${uploadId}/fail`,
      { error_reason: errorReason },
      dealId,
    )
  },

  uploadDealLogo: async (dealId: number, uploadId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(`${regionHost}/api/deals/${dealId}/logo`, {
      upload: uploadId,
    })
    return parseWithZod(dealResponseSchema, result)
  },

  removeDealLogo: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.delete(`${regionHost}/api/deals/${dealId}/logo`)
    return parseWithZod(dealResponseSchema, result)
  },

  fetchRawUrl: async (dealId: number, payload: UploadRawUrl) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(`${regionHost}/api/deals/${dealId}/raw_url`, payload)
    return parseWithZod(RawUrlResponseSchema, result)
  },

  fetchUploads: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}/uploads?is_ready=True`)
    return parseWithZod(UploadResponseSchema, result).uploads
  },

  // Unlike fetchUploads (is_ready=True → ready only), this omits the filter so
  // the backend returns every state, including errored uploads. Used where the
  // current state of an upload matters (e.g. showing "Unable to process").
  fetchAllUploads: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}/uploads`)
    return parseWithZod(UploadResponseSchema, result).uploads
  },

  fetchUpload: async (uploadId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/uploads/${uploadId}`, undefined, dealId)
    return parseWithZod(CompleteUploadSchema, result).upload
  },

  fetchRawPages: async (
    payload: {
      documents: Array<{
        id: string
        type: string
        pages: number[]
      }>
    },
    dealId: number,
  ) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(`${regionHost}/api/deals/${dealId}/raw_pages`, payload)
    return parseWithZod(RawPagesResponseSchema, result)
  },
}

const RawPagesResponseSchema = z.object({
  documents: z.array(
    z.object({
      id: z.number(),
      type: z.string(),
      state: z.string(),
      pages: z.array(
        z.object({
          page_number: z.number(),
          thumb: z.string().optional(),
          full: z.string().optional(),
        }),
      ),
    }),
  ),
  expires_in: z.number(),
})

const UploadFileResponseSchema = z.object({
  upload_id: z.number(),
  upload_url: z.string(),
})

const RawUrlResponseSchema = UploadRawUrlSchema.omit({ id: true }).extend({
  expires_in: z.number(),
  url: z.string(),
  id: z.number(),
})

type CompleteUploadPayload = {
  md5: string
  size: number
  should_match?: boolean
  upload_id: number
}
